import { generateNameSpaceColor } from "../../../utils/color";
import { getConversationAvatarText } from "../../../utils/conversationDisplay";

const MAX_VISIBLE_AVATAR_PARTICIPANTS = 4;

export const ConversationAvatar = ({
  participants,
  size = 40,
}: {
  participants: string[];
  size?: number;
}) => {
  const isGroup = participants.length > 1;
  const avatarStyle: React.CSSProperties = {
    color: "black",
    fontWeight: 700,
    position: "absolute",
    textAlign: "center",
  };
  const participantAvatar = (
    participant: string,
    style: React.CSSProperties,
    label = getConversationAvatarText([participant])
  ) => (
    <span
      key={participant || "additional-participants"}
      style={{
        ...avatarStyle,
        ...style,
        alignItems: "center",
        background: generateNameSpaceColor(participant || "additional-participants"),
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      {label}
    </span>
  );

  return (
    <div
      aria-label={
        isGroup
          ? `Group conversation with ${participants.length} participants`
          : "Conversation participant"
      }
      role="img"
      style={{
        flex: "0 0 auto",
        height: size,
        position: "relative",
        width: size,
      }}
    >
      {participants.length <= 1 &&
        participantAvatar(participants[0] || "", {
          fontSize: `${Math.max(12, Math.floor(size / 2.5))}px`,
          height: "100%",
          inset: 0,
          width: "100%",
        })}
      {participants.length === 2 && (
        <>
          {participantAvatar(participants[0], {
            boxShadow: "0 0 0 2px white",
            fontSize: `${Math.max(10, Math.floor(size / 3.4))}px`,
            height: "62%",
            left: 0,
            top: 0,
            width: "62%",
            zIndex: 1,
          })}
          {participantAvatar(participants[1], {
            boxShadow: "0 0 0 2px white",
            fontSize: `${Math.max(10, Math.floor(size / 3.4))}px`,
            height: "62%",
            right: 0,
            bottom: 0,
            width: "62%",
          })}
        </>
      )}
      {participants.length === 3 && (
        <>
          {participantAvatar(participants[0], {
            boxShadow: "0 0 0 2px white",
            fontSize: `${Math.max(9, Math.floor(size / 3.8))}px`,
            height: "56%",
            left: "22%",
            top: 0,
            width: "56%",
            zIndex: 1,
          })}
          {participantAvatar(participants[1], {
            boxShadow: "0 0 0 2px white",
            fontSize: `${Math.max(9, Math.floor(size / 3.8))}px`,
            bottom: 0,
            height: "56%",
            left: 0,
            width: "56%",
          })}
          {participantAvatar(participants[2], {
            boxShadow: "0 0 0 2px white",
            fontSize: `${Math.max(9, Math.floor(size / 3.8))}px`,
            bottom: 0,
            height: "56%",
            right: 0,
            width: "56%",
          })}
        </>
      )}
      {participants.length >= MAX_VISIBLE_AVATAR_PARTICIPANTS && (
        <>
          {Array.from(
            { length: MAX_VISIBLE_AVATAR_PARTICIPANTS },
            (_, index) => {
              const isCountCircle =
                participants.length > MAX_VISIBLE_AVATAR_PARTICIPANTS &&
                index === MAX_VISIBLE_AVATAR_PARTICIPANTS - 1;
              const participant = isCountCircle ? "" : participants[index];
              const label = isCountCircle
                ? `+${participants.length - (MAX_VISIBLE_AVATAR_PARTICIPANTS - 1)}`
                : getConversationAvatarText([participant]);

              return participantAvatar(
                participant,
                {
                  boxShadow: "0 0 0 1.5px white",
                  fontSize: `${Math.max(9, Math.floor(size / 4))}px`,
                  height: "48%",
                  left: index % 2 ? "52%" : 0,
                  top: index >= 2 ? "52%" : 0,
                  width: "48%",
                },
                label
              );
            }
          )}
        </>
      )}
    </div>
  );
};
